import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, getNewsById, getRelatedNews } from "@/lib/media";
import NewsCard from "@/components/base/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;

  usePageMeta({
    title: item ? `${item.title} - 影视资讯 - 西瓜影音` : "资讯详情 - 西瓜影音",
    description: item ? item.summary : "西瓜影音影视资讯详情页。",
    keywords: item ? `${item.category},${item.title},影视资讯,西瓜影音` : "影视资讯,西瓜影音",
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-16 md:py-24">
        <EmptyState
          icon="ri-file-unknow-line"
          title="没有找到这篇资讯"
          description="它可能已经被删除，或者链接有误。返回资讯列表看看其他内容吧。"
        >
          <Link
            to="/news"
            className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            返回资讯列表
          </Link>
        </EmptyState>
      </div>
    );
  }

  const related = getRelatedNews(item.id, item.category, 3);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10 animate-fade-in">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-400 mb-5" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="hover:text-primary-400 transition-colors">
          影视资讯
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-200 line-clamp-1">{item.category}</span>
      </nav>

      <article className="max-w-4xl">
        <span className="inline-block px-3 py-1 rounded-md bg-primary-500/15 text-primary-400 text-xs font-medium mb-4">
          {item.category}
        </span>
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 leading-tight mb-5">
          {item.title}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 pb-5 border-b border-background-800">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            {item.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line"></i>
            {formatDate(item.date)}
          </span>
        </div>

        <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-800 my-6">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 影视资讯配图`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <p className="text-base md:text-lg text-foreground-200 leading-relaxed mb-6 font-medium">
          {item.summary}
        </p>

        <div className="space-y-5">
          {item.content.map((paragraph, index) => (
            <p key={index} className="text-sm md:text-base text-foreground-300 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-background-800">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-800 hover:bg-background-700 text-foreground-100 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
        </div>
      </article>

      <section className="mt-14">
        <SectionHeading title="相关文章推荐" subtitle="继续阅读更多影视资讯" to="/news" linkLabel="更多资讯" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {related.map((news) => (
            <NewsCard key={news.id} item={news} variant="card" />
          ))}
        </div>
      </section>
    </div>
  );
}